import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

/**
 * ConfirmDialog
 * Props:
 * - open (bool)
 * - title (string)
 * - message (string | ReactNode)
 * - confirmLabel (string)
 * - cancelLabel (string)
 * - variant: 'danger' | 'default'
 * - loading (bool)
 * - onClose () => void
 * - onConfirm () => void
 */
export default function ConfirmDialog({
  open,
  title = 'Confirmar',
  message = '¿Estás seguro?',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  loading = false,
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  const isDanger = variant === 'danger';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-md animate-in fade-in duration-300">
      <div className="relative w-full max-w-md rounded-[1.5rem] sm:rounded-[2.5rem] bg-white dark:bg-[#1e1e1e] shadow-2xl border border-gray-100 dark:border-gray-800 transition-all transform scale-100 max-h-[95vh] overflow-y-auto">
        
        {/* Top bar accent */}
        <div className={`h-2 w-full ${isDanger ? 'bg-red-500' : 'bg-[#206a73]'}`} />

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 rounded-2xl ${isDanger ? 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400' : 'bg-[#206a73]/10 text-[#206a73]'}`}>
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {title}
            </h3>
          </div>

          <div className="mb-8">
            <div className="text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
              {message}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 rounded-2xl border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all active:scale-95"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`px-8 py-3 rounded-2xl text-white text-sm font-bold shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 min-w-[140px] ${
                isDanger 
                  ? 'bg-red-500 hover:bg-red-600 shadow-red-500/20' 
                  : 'bg-[#206a73] hover:bg-[#155059] shadow-[#206a73]/20'
              } disabled:opacity-60`}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Procesando...
                </span>
              ) : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
